import {
  Box,
  chakra,
  Icon,
  Input,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react'
import { ChevronDown, ChevronUp } from 'lucide-react'
import React, { useEffect, useMemo, useState } from 'react'

import { Checkbox } from './Checkbox'

export interface CheckboxMultiSelectOption {
  /** Unique value for this option (rendered as the option id). */
  id: number | string
  /** Primary label shown next to the checkbox. */
  label: string
  /** Optional muted secondary text rendered after the label in parentheses. */
  secondaryLabel?: string
  /** Optional list of extra strings searched when filtering. */
  searchText?: string[]
}

export interface CheckboxMultiSelectProps {
  /** The set of available options. */
  options: CheckboxMultiSelectOption[]
  /** Ids of the currently selected options. */
  selectedIds: ReadonlyArray<number | string> | ReadonlySet<number | string>
  /** Fires with the toggled option's id. The caller owns the selection state. */
  onToggle: (id: number | string) => void
  /** Disables all checkboxes and prevents hover styling. */
  disabled?: boolean
  /** Renders a loading spinner in place of the list. */
  isLoading?: boolean
  /** Shown in the collapsed summary row when there is no selection. */
  placeholder?: string
  /** Formats the summary shown in the collapsed header row. */
  renderSummary?: (selectedCount: number, totalCount: number) => string
  /** Search input placeholder. */
  searchPlaceholder?: string
  /** Message shown when options is empty. */
  emptyLabel?: string
  /** Message shown when the filter returns no results. */
  noResultsLabel?: string
  /** Formats the "N of M selected" footer. Pass null/undefined to hide it. */
  renderFooter?:
    ((selectedCount: number, totalCount: number) => string | null) | null
  /** Maximum height of the scrollable list. Defaults to `280px`. */
  maxListHeight?: string | number
  /**
   * Whether the list is collapsible via the header row. Defaults to `true`.
   * When `false`, the list is always shown (no summary row rendered).
   */
  collapsible?: boolean
  /** Controlled expansion state. Uncontrolled when omitted. */
  isOpen?: boolean
  /** Called when the user toggles the expansion state. */
  onOpenChange?: (open: boolean) => void
  /** Initial expansion state when uncontrolled. Defaults to `true`. */
  defaultOpen?: boolean
}

const defaultRenderSummary = (count: number) =>
  count > 0
    ? `${count} item${count === 1 ? '' : 's'} selected`
    : 'Select items...'

const defaultRenderFooter = (count: number, total: number) =>
  `${count} of ${total} selected`

const normalizeSelected = (
  selected: CheckboxMultiSelectProps['selectedIds'],
): Set<number | string> => new Set(selected)

/**
 * Reusable multi-select list of checkboxes with a filter input, collapsible
 * summary row, and "N of M" footer. Used for things like picking members,
 * user groups, roles, etc. State is fully controlled by the caller (selected
 * ids + onToggle).
 */
export const CheckboxMultiSelect: React.FC<CheckboxMultiSelectProps> = ({
  options,
  selectedIds,
  onToggle,
  disabled = false,
  isLoading = false,
  placeholder,
  renderSummary = defaultRenderSummary,
  searchPlaceholder = 'Filter...',
  emptyLabel = 'No options available.',
  noResultsLabel = 'No options match your filter.',
  renderFooter = defaultRenderFooter,
  maxListHeight = '280px',
  collapsible = true,
  isOpen: controlledOpen,
  onOpenChange,
  defaultOpen = true,
}) => {
  const selectedSet = useMemo(
    () => normalizeSelected(selectedIds),
    [selectedIds],
  )
  const selectedCount = selectedSet.size
  const totalCount = options.length

  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen)
  const isControlled = controlledOpen !== undefined
  const open = isControlled ? controlledOpen : uncontrolledOpen
  const setOpen = (next: boolean) => {
    if (!isControlled) setUncontrolledOpen(next)
    onOpenChange?.(next)
  }

  const [search, setSearch] = useState('')
  // Reset filter when the underlying set of options actually changes (e.g.
  // the caller switches which PBX's user groups are shown). Keying on a
  // stable derived value rather than the `options` array reference keeps
  // callers that pass an inline array literal from wiping the filter on
  // every parent re-render.
  const optionsKey = useMemo(
    () => options.map((option) => option.id).join('\u0000'),
    [options],
  )
  useEffect(() => {
    setSearch('')
  }, [optionsKey])
  const filteredOptions = useMemo(() => {
    const query = search.trim().toLowerCase()
    if (!query) return options
    return options.filter((option) => {
      const haystack = [
        option.label,
        option.secondaryLabel ?? '',
        ...(option.searchText ?? []),
      ]
      return haystack.some((value) => value.toLowerCase().includes(query))
    })
  }, [options, search])

  if (isLoading) {
    return (
      <Box textAlign="center" py={4}>
        <Spinner size="sm" />
        <Text mt={2} fontSize="sm" color="fg.muted">
          Loading...
        </Text>
      </Box>
    )
  }

  if (totalCount === 0) {
    return (
      <Text fontSize="sm" color="fg.muted">
        {emptyLabel}
      </Text>
    )
  }

  const summaryText =
    selectedCount > 0 ? renderSummary(selectedCount, totalCount) : placeholder
  const resolvedSummary =
    summaryText ?? renderSummary(selectedCount, totalCount)

  const footerText = renderFooter
    ? renderFooter(selectedCount, totalCount)
    : null

  const list = (
    <Box
      p={2}
      borderWidth="1px"
      borderColor="border.default"
      bg="bg.default"
      borderRadius="md"
      boxShadow="sm"
    >
      <VStack align="stretch" gap={2}>
        <Input
          placeholder={searchPlaceholder}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="sm"
          bg="bg.subtle"
          borderColor="border.default"
          borderRadius="sm"
          _placeholder={{ color: 'fg.subtle' }}
          _hover={{ borderColor: 'border.emphasized' }}
          _focusVisible={{
            borderColor: 'brand.solid',
            boxShadow: '0 0 0 2px var(--chakra-colors-brand-solid)',
            outline: 'none',
          }}
        />
        <Box maxH={maxListHeight} overflowY="auto" pr={1}>
          <VStack align="stretch" gap={1}>
            {filteredOptions.map((option) => {
              const isChecked = selectedSet.has(option.id)
              return (
                <Box
                  key={option.id}
                  px={2}
                  py={1.5}
                  borderRadius="sm"
                  _hover={{ bg: disabled ? undefined : 'bg.subtle' }}
                >
                  <Checkbox
                    checked={isChecked}
                    onCheckedChange={() => onToggle(option.id)}
                    disabled={disabled}
                    size="sm"
                    display="flex"
                    alignItems="flex-start"
                    gap={0}
                    w="full"
                    css={rowLayoutCss}
                  >
                    <Text fontSize="sm" color="fg.default">
                      <Text as="span" fontWeight="medium">
                        {option.label}
                      </Text>
                      {option.secondaryLabel && (
                        <Text as="span" color="fg.muted" fontWeight="normal">
                          {' '}
                          ({option.secondaryLabel})
                        </Text>
                      )}
                    </Text>
                  </Checkbox>
                </Box>
              )
            })}
            {filteredOptions.length === 0 && (
              <Text fontSize="sm" color="fg.muted" px={2} py={2}>
                {noResultsLabel}
              </Text>
            )}
          </VStack>
        </Box>
        {footerText !== null && footerText !== undefined && (
          <Text fontSize="xs" color="fg.muted" px={1}>
            {footerText}
          </Text>
        )}
      </VStack>
    </Box>
  )

  if (!collapsible) {
    return list
  }

  return (
    <VStack align="stretch" gap={2}>
      <SummaryButton
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <Text
          fontSize="sm"
          color={selectedCount > 0 ? 'fg.default' : 'fg.muted'}
          fontWeight={selectedCount > 0 ? 'medium' : 'normal'}
        >
          {resolvedSummary}
        </Text>
        <Icon color="fg.default">
          {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </Icon>
      </SummaryButton>
      {open && list}
    </VStack>
  )
}

const rowLayoutCss = {
  '& [data-part="control"]': { marginTop: '2px' },
  '& [data-part="label"]': { flex: '1', minWidth: 0 },
}

const SummaryButton = chakra('button', {
  base: {
    h: '40px',
    px: 3,
    borderWidth: '1px',
    borderColor: 'border.default',
    borderRadius: 'md',
    bg: 'bg.subtle',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    w: 'full',
    textAlign: 'left',
    _hover: { borderColor: 'border.emphasized' },
    _focusVisible: {
      borderColor: 'brand.solid',
      boxShadow: '0 0 0 2px var(--chakra-colors-brand-solid)',
      outline: 'none',
    },
  },
})
